import type { UpdateSocialInfoRequestModel } from "@payping/api";
import { Instagram, Telegram, Twitter } from "@payping/icons";
import { getUserSocials, updateSocials } from "@payping/redux/actions";
import { DashboardRoutes } from "@/router/constant";
import {
  Button,
  Image,
  Input,
  LegacyForm,
  Space,
  Tooltip,
  Typography,
  useMessagedAsync,
  useMessagedAsyncFn,
} from "@payping/web-components";
import React, { useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useAppTranslation } from "@/utils/useAppTranslation";
import { useAppDispatch, useAppSelector } from "@/utils";
import { URLS } from "@payping/utilities-shared/consts";

const SocialSection: React.FC = () => {
  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();
  const socials = useAppSelector((state) => state.user.socials);
  const [form] = LegacyForm.useForm<UpdateSocialInfoRequestModel>();
  const navigate = useNavigate();

  const { loading: loadingGet } = useMessagedAsync(
    async () => {
      await dispatch(getUserSocials());
    },
    [dispatch],
    { useProgress: true },
  );

  const [{ loading: loadingEdit }, handleEdit] = useMessagedAsyncFn(
    async (values: UpdateSocialInfoRequestModel) => {
      await dispatch(updateSocials(values));
      navigate(DashboardRoutes.profile.root.url);
    },
    [dispatch, navigate],
    undefined,
    { useProgress: true },
  );

  const loading = useMemo(() => loadingGet || loadingEdit, [loadingGet, loadingEdit]);

  useEffect(() => {
    form.setFieldsValue({
      instagram: socials?.instagram,
      telegram: socials?.telegram,
      twitter: socials?.twitter,
    });
  }, [form, socials]);

  return (
    <Space orientation="vertical" size={"middle"} className={"tw:w-full"}>
      <Typography.Title level={4} className="tw:font-medium">
        {t("profile.main.social")}
      </Typography.Title>
      <LegacyForm<UpdateSocialInfoRequestModel>
        onFinish={handleEdit}
        form={form}
        className="tw:flex tw:flex-col tw:items-start"
        layout="horizontal"
      >
        <LegacyForm.Item className="tw:flex tw:justify-center">
          <Image preview={false} src={`${URLS.STORAGE_URL}/img/settings/settings-social-bg.png`} />
        </LegacyForm.Item>
        <LegacyForm.Item
          label={
            <Tooltip title={"Twitter"}>
              <Twitter width={"20px"} />
            </Tooltip>
          }
          name={"twitter"}
        >
          <Input disabled={loading} style={{ direction: "ltr" }} placeholder={"twitter"} />
        </LegacyForm.Item>
        <LegacyForm.Item
          label={
            <Tooltip title={"Telegram"}>
              <Telegram width={"20px"} />
            </Tooltip>
          }
          name={"telegram"}
        >
          <Input disabled={loading} style={{ direction: "ltr" }} placeholder={"telegram ID"} />
        </LegacyForm.Item>
        <LegacyForm.Item
          label={
            <Tooltip title={"Instagram"}>
              <Instagram width={"20px"} />
            </Tooltip>
          }
          name={"instagram"}
        >
          <Input disabled={loading} style={{ direction: "ltr" }} placeholder={"instagram"} />
        </LegacyForm.Item>
        <Space>
          <Button type={"primary"} loading={loading} htmlType={"submit"}>
            {t("save")}
          </Button>
          <Link to={DashboardRoutes.profile.root.url}>
            <Button variant={"outlined"} color="primary">
              {t("return")}
            </Button>
          </Link>
        </Space>
      </LegacyForm>
    </Space>
  );
};

export default SocialSection;
